import type { components } from "@libs/ticketing-client";

/**
 * The generated client preserves the historical OpenAPI `allOf` nullable
 * shape for `quote`, which TypeScript represents as an impossible
 * intersection. The wire is still nullable; keep that correction local to
 * the browser adapter while leaving the published schema unchanged.
 */
export type BuyerSession = Omit<
  components["schemas"]["BuyerCheckoutSession"],
  "quote"
> & {
  quote: components["schemas"]["Quote"] | null;
};

export type CheckoutState =
  | { kind: "loading" }
  | { kind: "cart"; session: BuyerSession }
  | { kind: "finalizing"; session: BuyerSession }
  | { kind: "success"; session: BuyerSession }
  | { kind: "expired"; session: BuyerSession }
  | { kind: "refused"; session: BuyerSession }
  | { kind: "processing_timeout"; session: BuyerSession }
  | { kind: "error"; message: string };

export type CheckoutEvent =
  | { type: "session_loaded"; session: BuyerSession }
  | { type: "session_updated"; session: BuyerSession }
  | { type: "payment_confirmed" }
  | { type: "timeout" }
  | { type: "failure"; message: string };

/** The session will not change again: issued, expired, or the payment
 *  refused. */
export const isSessionFinal = (session: BuyerSession) =>
  terminalState(session) !== undefined;

function terminalState(session: BuyerSession): CheckoutState | undefined {
  if (session.status === "completed") return { kind: "success", session };
  if (session.status === "expired") return { kind: "expired", session };
  if (session.status === "payment_refused") return { kind: "refused", session };
  return undefined;
}

function sessionState(session: BuyerSession): CheckoutState {
  const terminal = terminalState(session);
  if (terminal) return terminal;
  if (
    session.status === "payment_pending" ||
    session.status === "payment_processing"
  ) {
    return { kind: "finalizing", session };
  }
  return { kind: "cart", session };
}

export function checkoutReducer(
  state: CheckoutState,
  event: CheckoutEvent,
): CheckoutState {
  switch (event.type) {
    case "session_loaded":
      return sessionState(event.session);
    case "session_updated": {
      const terminal = terminalState(event.session);
      if (terminal) return terminal;
      // A session that has ended stays ended: a read that says otherwise was
      // answered from before it ended, however late it was made (reads and
      // the engine's settling cross on the network).
      if (
        state.kind === "success" ||
        state.kind === "expired" ||
        state.kind === "refused"
      )
        return state;
      if (
        event.session.status === "payment_pending" ||
        event.session.status === "payment_processing"
      ) {
        return { kind: "finalizing", session: event.session };
      }
      if (state.kind === "finalizing") {
        return { kind: "finalizing", session: event.session };
      }
      return { kind: "cart", session: event.session };
    }
    case "payment_confirmed":
      // Every payment confirms on the connected account from the cart: a
      // saved card, a new card or an express wallet, all through the same
      // platform SetupIntent and clone (LIS-699).
      return state.kind === "cart"
        ? { kind: "finalizing", session: state.session }
        : state;
    case "timeout":
      return state.kind === "finalizing"
        ? { kind: "processing_timeout", session: state.session }
        : state;
    case "failure":
      return { kind: "error", message: event.message };
  }
}
